import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Drawer,
    IconButton,
    Skeleton,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useDispatch, useSelector } from 'react-redux';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import {
    approveBrandOpsCalendarItem,
    createBrandOpsCalendarItem,
    deleteBrandOpsCalendarItem,
    fetchBrandOpsCalendar,
    publishBrandOpsCalendarItem,
} from '../../../redux/brandOps/actions';
import type { IBrandOpsCalendarItem } from '../../../redux/brandOps/types';
import {
    brandOpsIsBrandAdminSelector,
    brandOpsCalendarSelector,
    brandOpsFetchingCalendarSelector,
} from '../../../redux/brandOps/selectors';
import { showToastMessage, useDispatchPromise } from '../../../utils/AppUtils';
import {
    BRAND_LUXE_MUTED,
    BRAND_LUXE_PRIMARY,
    BRAND_SURFACE_CONTAINER,
    BRAND_SURFACE_CONTAINER_HIGH,
} from '../brandDashboardTokens';
import InstagramComposeModal, { type InstagramComposeResult } from './InstagramComposeModal';
import { ROUTES } from '../../../types/global/routes.types';
import {
    BrandManageGate,
    ManageGoldButton,
    ManagePageHeader,
    MANAGE_PAGE_SX,
    StatusChip,
    useBrandManageGate,
} from './manageShared';

const metaBadgeSx = {
    px: 1.25,
    py: 0.4,
    bgcolor: 'rgba(255,255,255,0.06)',
    borderRadius: 1,
    fontSize: 11,
    color: 'rgba(255,255,255,0.6)',
    fontFamily: FONT_ENUM.MAIN_MEDIUM,
    textTransform: 'capitalize' as const,
    letterSpacing: '0.02em',
};

const sectionLabelSx = {
    color: BRAND_LUXE_MUTED,
    fontSize: 10,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.08em',
    mb: 0.75,
    fontFamily: FONT_ENUM.MAIN_MEDIUM,
};

function startOfWeek(d: Date) {
    const x = new Date(d);
    const day = x.getDay();
    x.setDate(x.getDate() - day);
    x.setHours(0, 0, 0, 0);
    return x;
}

function addDays(d: Date, n: number) {
    const x = new Date(d);
    x.setDate(x.getDate() + n);
    return x;
}

function sameDay(a: Date, b: Date) {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function statusTone(s: string) {
    if (s === 'approved' || s === 'published') return 'mint' as const;
    if (s === 'pending_approval' || s === 'scheduled') return 'gold' as const;
    if (s === 'rejected' || s === 'missed' || s === 'failed') return 'error' as const;
    return 'muted' as const;
}

export default function BrandManageCalendarPage() {
    const dispatch = useDispatch();
    const dispatchP = useDispatchPromise();
    const { brandId } = useBrandManageGate();
    const items = useSelector(brandOpsCalendarSelector);
    const fetching = useSelector(brandOpsFetchingCalendarSelector);
    const isBrandAdmin = useSelector(brandOpsIsBrandAdminSelector);
    const [mode, setMode] = useState<'week' | 'month'>('week');
    const [anchor, setAnchor] = useState(() => new Date());
    const [composeOpen, setComposeOpen] = useState(false);
    const [selected, setSelected] = useState<IBrandOpsCalendarItem | null>(null);
    const [publishing, setPublishing] = useState(false);
    const [approving, setApproving] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState(false);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        if (brandId) dispatch(fetchBrandOpsCalendar({ brandId, force: true }));
    }, [dispatch, brandId]);

    const days = useMemo(() => {
        if (mode === 'week') {
            const start = startOfWeek(anchor);
            return Array.from({ length: 7 }, (_, i) => addDays(start, i));
        }
        const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
        const start = startOfWeek(first);
        return Array.from({ length: 42 }, (_, i) => addDays(start, i));
    }, [mode, anchor]);

    const itemsByDay = (day: Date) =>
        items.filter(it => {
            if (!it.scheduled_at) return false;
            return sameDay(new Date(it.scheduled_at), day);
        });

    const onCompose = async (result: InstagramComposeResult) => {
        if (!brandId) return;
        if (!result.publishNow && !result.scheduledAt) {
            showToastMessage('Schedule time is required', 'error');
            return;
        }
        try {
            const created = (await dispatchP(
                createBrandOpsCalendarItem({
                    brandId,
                    body: {
                        title: result.title || result.caption.slice(0, 48) || `${result.platform} post`,
                        caption: result.caption,
                        description: result.description,
                        hashtags: result.hashtags,
                        scheduled_at: new Date(result.scheduledAt).toISOString(),
                        platform: result.platform,
                        format: result.format,
                        aspect_ratio: result.aspect,
                        media_urls: result.mediaUrls,
                        media_type: result.mediaType,
                        platform_options: result.platformOptions,
                        ...(result.projectId ? { project_id: result.projectId } : {}),
                    },
                }),
            )) as any;
            if (result.publishNow && created?._id) {
                await dispatchP(publishBrandOpsCalendarItem({ brandId, itemId: created._id }));
                showToastMessage('Posted — queued for publishing', 'success');
            } else {
                showToastMessage('Calendar item created', 'success');
            }
            dispatch(fetchBrandOpsCalendar({ brandId, force: true }));
        } catch (err: any) {
            showToastMessage(err?.message || 'Could not create calendar item', 'error');
        }
    };

    const approve = async () => {
        if (!brandId || !selected) return;
        setApproving(true);
        try {
            await dispatchP(approveBrandOpsCalendarItem({ brandId, itemId: selected._id }));
            showToastMessage('Approved', 'success');
            setSelected(null);
            dispatch(fetchBrandOpsCalendar({ brandId, force: true }));
        } catch (err: any) {
            showToastMessage(err?.message || 'Approval failed', 'error');
        } finally {
            setApproving(false);
        }
    };

    const publish = async () => {
        if (!brandId || !selected) return;
        setPublishing(true);
        try {
            await dispatchP(publishBrandOpsCalendarItem({ brandId, itemId: selected._id }));
            showToastMessage('Queued for publish', 'success');
            setSelected(null);
            dispatch(fetchBrandOpsCalendar({ brandId, force: true }));
        } catch (err: any) {
            showToastMessage(err?.message || 'Publish failed', 'error');
        } finally {
            setPublishing(false);
        }
    };

    const confirmDelete = async () => {
        if (!brandId || !selected) return;
        setDeleting(true);
        try {
            await dispatchP(deleteBrandOpsCalendarItem({ brandId, itemId: selected._id }));
            showToastMessage('Post deleted', 'success');
            setDeleteConfirm(false);
            setSelected(null);
        } catch (err: any) {
            showToastMessage(err?.message || 'Delete failed', 'error');
        } finally {
            setDeleting(false);
        }
    };

    return (
        <BrandManageGate>
            <Box sx={MANAGE_PAGE_SX}>
                <ManagePageHeader
                    title="Calendar"
                    subtitle="Week / month grid with approval and compose."
                    back={ROUTES.BRAND_MANAGE}
                    action={
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <ToggleButtonGroup
                                exclusive
                                size="small"
                                value={mode}
                                onChange={(_, v) => v && setMode(v)}
                                sx={{
                                    '& .MuiToggleButton-root': {
                                        color: BRAND_LUXE_MUTED,
                                        borderColor: 'rgba(255,255,255,0.12)',
                                        '&.Mui-selected': {
                                            color: BRAND_LUXE_PRIMARY,
                                            bgcolor: 'rgba(255,201,101,0.12)',
                                        },
                                    },
                                }}>
                                <ToggleButton value="week">Week</ToggleButton>
                                <ToggleButton value="month">Month</ToggleButton>
                            </ToggleButtonGroup>
                            <ManageGoldButton onClick={() => setComposeOpen(true)}>Compose</ManageGoldButton>
                        </Box>
                    }
                />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                    <ManageGoldButton
                        sx={{ bgcolor: 'transparent', color: BRAND_LUXE_MUTED }}
                        onClick={() => setAnchor(addDays(anchor, mode === 'week' ? -7 : -30))}>
                        Prev
                    </ManageGoldButton>
                    <Typography sx={{ color: '#fff', fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: 13 }}>
                        {anchor.toLocaleString(undefined, { month: 'long', year: 'numeric' })}
                    </Typography>
                    <ManageGoldButton
                        sx={{ bgcolor: 'transparent', color: BRAND_LUXE_MUTED }}
                        onClick={() => setAnchor(addDays(anchor, mode === 'week' ? 7 : 30))}>
                        Next
                    </ManageGoldButton>
                </Box>

                {fetching && !items.length ? (
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1 }}>
                        {Array.from({ length: mode === 'week' ? 7 : 42 }).map((_, i) => (
                            <Box
                                key={i}
                                sx={{
                                    minHeight: mode === 'week' ? 120 : 88,
                                    bgcolor: BRAND_SURFACE_CONTAINER,
                                    borderRadius: 2,
                                    p: 1,
                                    border: '1px solid rgba(255,255,255,0.04)',
                                }}>
                                <Skeleton
                                    variant="text"
                                    width="55%"
                                    sx={{ bgcolor: 'rgba(255,255,255,0.06)', mb: 0.75, borderRadius: 0.5 }}
                                />
                                {i % 3 === 0 && (
                                    <Skeleton
                                        variant="rounded"
                                        height={22}
                                        sx={{ bgcolor: 'rgba(255,201,101,0.07)', borderRadius: 1 }}
                                    />
                                )}
                            </Box>
                        ))}
                    </Box>
                ) : (
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1 }}>
                        {days.map(day => {
                            const dayItems = itemsByDay(day);
                            const inMonth = day.getMonth() === anchor.getMonth();
                            return (
                                <Box
                                    key={day.toISOString()}
                                    sx={{
                                        minHeight: mode === 'week' ? 120 : 88,
                                        bgcolor: BRAND_SURFACE_CONTAINER,
                                        borderRadius: 2,
                                        p: 1,
                                        opacity: mode === 'month' && !inMonth ? 0.45 : 1,
                                        border: sameDay(day, new Date())
                                            ? `1px solid ${BRAND_LUXE_PRIMARY}`
                                            : '1px solid rgba(255,255,255,0.04)',
                                    }}>
                                    <Typography
                                        sx={{
                                            fontSize: 11,
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            color: sameDay(day, new Date()) ? BRAND_LUXE_PRIMARY : BRAND_LUXE_MUTED,
                                            mb: 0.75,
                                        }}>
                                        {day.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric' })}
                                    </Typography>
                                    {dayItems.map(it => {
                                        const thumb = it.media_urls?.[0];
                                        return (
                                            <Box
                                                key={it._id}
                                                onClick={() => setSelected(it)}
                                                sx={{
                                                    bgcolor: BRAND_SURFACE_CONTAINER_HIGH,
                                                    borderRadius: 1,
                                                    mb: 0.5,
                                                    cursor: 'pointer',
                                                    overflow: 'hidden',
                                                    border: '1px solid rgba(255,255,255,0.05)',
                                                    '&:hover': { border: `1px solid ${BRAND_LUXE_PRIMARY}40` },
                                                    transition: 'border-color 0.15s',
                                                }}>
                                                {thumb && (
                                                    <Box
                                                        component="img"
                                                        src={thumb}
                                                        alt=""
                                                        onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                                                            (e.currentTarget as HTMLImageElement).style.display =
                                                                'none';
                                                        }}
                                                        sx={{
                                                            width: '100%',
                                                            height: mode === 'week' ? 42 : 36,
                                                            objectFit: 'cover',
                                                            display: 'block',
                                                            objectPosition: 'top',
                                                        }}
                                                    />
                                                )}
                                                <Box sx={{ px: 0.5, pt: 0.4, pb: 0.5 }}>
                                                    <Typography
                                                        sx={{
                                                            fontSize: 9,
                                                            color: BRAND_LUXE_PRIMARY,
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                            lineHeight: 1.3,
                                                        }}>
                                                        {it.title || it.caption || 'Post'}
                                                    </Typography>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 0.4,
                                                            mt: 0.2,
                                                        }}>
                                                        <Box
                                                            sx={{
                                                                width: 3,
                                                                height: 3,
                                                                borderRadius: '50%',
                                                                flexShrink: 0,
                                                                bgcolor:
                                                                    it.status === 'published'
                                                                        ? '#34d399'
                                                                        : it.status === 'failed' ||
                                                                          it.status === 'missed'
                                                                        ? '#f87171'
                                                                        : it.status === 'approved' ||
                                                                          it.status === 'scheduled'
                                                                        ? BRAND_LUXE_PRIMARY
                                                                        : 'rgba(255,255,255,0.3)',
                                                            }}
                                                        />
                                                        <Typography
                                                            sx={{
                                                                fontSize: 8,
                                                                color: 'rgba(255,255,255,0.3)',
                                                                textTransform: 'capitalize',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap',
                                                            }}>
                                                            {it.status?.replace(/_/g, ' ')}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            );
                        })}
                    </Box>
                )}

                {!items.length && !fetching ? (
                    <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 13, mt: 2 }}>
                        No posts scheduled yet. Use the compose button to create your first post.
                    </Typography>
                ) : null}
            </Box>

            <Drawer
                anchor="right"
                open={!!selected}
                onClose={() => setSelected(null)}
                PaperProps={{
                    sx: {
                        width: { xs: '100%', sm: 400 },
                        bgcolor: '#0e0e0e',
                        display: 'flex',
                        flexDirection: 'column',
                        borderLeft: '1px solid rgba(255,255,255,0.06)',
                    },
                }}>
                {selected ? (
                    <>
                        {/* Header */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                px: 2.5,
                                py: 1.75,
                                borderBottom: '1px solid rgba(255,255,255,0.06)',
                                flexShrink: 0,
                            }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {selected.platform && <Box sx={metaBadgeSx}>{selected.platform}</Box>}
                                <StatusChip
                                    label={selected.status.replace(/_/g, ' ')}
                                    tone={statusTone(selected.status)}
                                />
                            </Box>
                            <IconButton
                                size="small"
                                onClick={() => setSelected(null)}
                                sx={{ color: BRAND_LUXE_MUTED, '&:hover': { color: '#fff' } }}>
                                <CloseIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Box>

                        {/* Scrollable body */}
                        <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, py: 2.5 }}>
                            {/* Title + date */}
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    color: '#fff',
                                    fontSize: 17,
                                    lineHeight: 1.3,
                                    mb: 0.5,
                                }}>
                                {selected.title || 'Post'}
                            </Typography>
                            <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 12, mb: 2.5 }}>
                                {selected.scheduled_at
                                    ? new Date(selected.scheduled_at).toLocaleString(undefined, {
                                          weekday: 'short',
                                          month: 'short',
                                          day: 'numeric',
                                          year: 'numeric',
                                          hour: 'numeric',
                                          minute: '2-digit',
                                      })
                                    : 'No schedule set'}
                            </Typography>

                            {/* Media preview */}
                            {selected.media_urls?.length ? (
                                <Box
                                    sx={{
                                        mb: 2.5,
                                        borderRadius: 2,
                                        overflow: 'hidden',
                                        position: 'relative',
                                        bgcolor: BRAND_SURFACE_CONTAINER,
                                    }}>
                                    <Box
                                        component="img"
                                        src={selected.media_urls[0]}
                                        sx={{
                                            width: '100%',
                                            aspectRatio: selected.aspect_ratio
                                                ? selected.aspect_ratio.replace(':', '/')
                                                : '1 / 1',
                                            objectFit: 'cover',
                                            display: 'block',
                                        }}
                                        onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                                            (e.currentTarget as HTMLImageElement).style.display = 'none';
                                        }}
                                    />
                                    {selected.media_urls.length > 1 && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: 8,
                                                right: 8,
                                                bgcolor: 'rgba(0,0,0,0.65)',
                                                color: '#fff',
                                                fontSize: 11,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                px: 1,
                                                py: 0.3,
                                                borderRadius: 1,
                                            }}>
                                            +{selected.media_urls.length - 1} more
                                        </Box>
                                    )}
                                </Box>
                            ) : null}

                            {/* Format / type badges */}
                            {(selected.format || selected.media_type || selected.aspect_ratio) && (
                                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 2.5 }}>
                                    {selected.format && (
                                        <Box sx={metaBadgeSx}>{selected.format.replace(/_/g, ' ')}</Box>
                                    )}
                                    {selected.media_type && <Box sx={metaBadgeSx}>{selected.media_type}</Box>}
                                    {selected.aspect_ratio && <Box sx={metaBadgeSx}>{selected.aspect_ratio}</Box>}
                                </Box>
                            )}

                            {/* Caption */}
                            {selected.caption ? (
                                <Box sx={{ mb: 2.5 }}>
                                    <Typography sx={sectionLabelSx}>Caption</Typography>
                                    <Box
                                        sx={{
                                            bgcolor: BRAND_SURFACE_CONTAINER,
                                            borderRadius: 1.5,
                                            p: 1.5,
                                            border: '1px solid rgba(255,255,255,0.05)',
                                        }}>
                                        <Typography
                                            sx={{
                                                color: 'rgba(255,255,255,0.82)',
                                                fontSize: 13,
                                                lineHeight: 1.65,
                                                whiteSpace: 'pre-wrap',
                                                wordBreak: 'break-word',
                                            }}>
                                            {selected.caption}
                                        </Typography>
                                    </Box>
                                </Box>
                            ) : null}

                            {/* Hashtags */}
                            {selected.hashtags?.length ? (
                                <Box sx={{ mb: 2.5 }}>
                                    <Typography sx={sectionLabelSx}>Hashtags</Typography>
                                    <Typography
                                        sx={{
                                            color: BRAND_LUXE_PRIMARY,
                                            fontSize: 13,
                                            lineHeight: 1.8,
                                            wordBreak: 'break-word',
                                        }}>
                                        {selected.hashtags.map(h => `#${h}`).join('  ')}
                                    </Typography>
                                </Box>
                            ) : null}
                        </Box>

                        {/* Footer actions */}
                        <Box
                            sx={{
                                px: 2.5,
                                pt: 2,
                                pb: 2.5,
                                borderTop: '1px solid rgba(255,255,255,0.06)',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1,
                                flexShrink: 0,
                            }}>
                            {/* Approve — only for brand admins */}
                            {selected.status === 'pending_approval' || selected.status === 'draft' ? (
                                isBrandAdmin ? (
                                    <ManageGoldButton onClick={approve} disabled={approving}>
                                        {approving ? (
                                            <CircularProgress size={16} sx={{ color: '#1a1919' }} />
                                        ) : (
                                            'Approve'
                                        )}
                                    </ManageGoldButton>
                                ) : (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            bgcolor: 'rgba(231,132,16,0.08)',
                                            border: '1px solid rgba(231,132,16,0.2)',
                                            borderRadius: 1.5,
                                            px: 1.5,
                                            py: 1.25,
                                        }}>
                                        <Box
                                            sx={{
                                                width: 6,
                                                height: 6,
                                                borderRadius: '50%',
                                                bgcolor: BRAND_LUXE_PRIMARY,
                                                flexShrink: 0,
                                            }}
                                        />
                                        <Typography sx={{ fontSize: 12, color: BRAND_LUXE_PRIMARY, lineHeight: 1.4 }}>
                                            Awaiting brand approval before this post can be published.
                                        </Typography>
                                    </Box>
                                )
                            ) : null}

                            {/* Publish / retry */}
                            {(selected.status === 'approved' ||
                                selected.status === 'scheduled' ||
                                selected.status === 'missed' ||
                                selected.status === 'failed') &&
                            selected.internal_status !== 'queued' ? (
                                <ManageGoldButton onClick={publish} disabled={publishing}>
                                    {publishing ? (
                                        <CircularProgress size={16} sx={{ color: '#1a1919' }} />
                                    ) : selected.status === 'missed' || selected.status === 'failed' ? (
                                        'Retry publish'
                                    ) : (
                                        'Publish / queue'
                                    )}
                                </ManageGoldButton>
                            ) : selected.internal_status === 'queued' ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                                    <CircularProgress size={12} sx={{ color: BRAND_LUXE_PRIMARY }} />
                                    <Typography sx={{ fontSize: 12, color: BRAND_LUXE_MUTED }}>
                                        Queued for publishing
                                    </Typography>
                                </Box>
                            ) : null}

                            {/* Delete + Compose new — side by side */}
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                {!['queued', 'published'].includes(selected.status) && (
                                    <Box
                                        onClick={() => setDeleteConfirm(true)}
                                        sx={{
                                            flex: 1,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: 0.75,
                                            py: 0.9,
                                            borderRadius: 1.5,
                                            border: '1px solid rgba(239,68,68,0.2)',
                                            color: 'rgba(239,68,68,0.6)',
                                            fontSize: 13,
                                            cursor: 'pointer',
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            '&:hover': {
                                                bgcolor: 'rgba(239,68,68,0.06)',
                                                borderColor: 'rgba(239,68,68,0.4)',
                                                color: '#f87171',
                                            },
                                            transition: 'all 0.15s',
                                        }}>
                                        <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                                        Delete
                                    </Box>
                                )}
                                <ManageGoldButton
                                    sx={{
                                        flex: 1,
                                        bgcolor: 'transparent',
                                        color: BRAND_LUXE_MUTED,
                                        border: '1px solid rgba(255,255,255,0.1)',
                                        '&:hover': { borderColor: 'rgba(255,255,255,0.25)', color: '#fff' },
                                    }}
                                    onClick={() => {
                                        setSelected(null);
                                        setComposeOpen(true);
                                    }}>
                                    Compose new
                                </ManageGoldButton>
                            </Box>
                        </Box>
                    </>
                ) : null}
            </Drawer>

            <InstagramComposeModal open={composeOpen} onClose={() => setComposeOpen(false)} onSubmit={onCompose} />

            <Dialog
                open={deleteConfirm}
                onClose={() => !deleting && setDeleteConfirm(false)}
                PaperProps={{
                    sx: {
                        bgcolor: '#1a1a2e',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: 2,
                        minWidth: 320,
                    },
                }}>
                <DialogTitle sx={{ color: '#fff', fontSize: 15, pb: 1 }}>Delete post?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 13 }}>
                        This will permanently remove the calendar post. This cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
                    <Box
                        onClick={() => !deleting && setDeleteConfirm(false)}
                        sx={{
                            px: 2,
                            py: 0.75,
                            borderRadius: 1.5,
                            border: '1px solid rgba(255,255,255,0.12)',
                            color: BRAND_LUXE_MUTED,
                            fontSize: 13,
                            cursor: 'pointer',
                            '&:hover': { color: '#fff', borderColor: 'rgba(255,255,255,0.3)' },
                        }}>
                        Cancel
                    </Box>
                    <Box
                        onClick={confirmDelete}
                        sx={{
                            px: 2,
                            py: 0.75,
                            borderRadius: 1.5,
                            bgcolor: 'rgba(239,68,68,0.15)',
                            border: '1px solid rgba(239,68,68,0.4)',
                            color: '#f87171',
                            fontSize: 13,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.75,
                            '&:hover': { bgcolor: 'rgba(239,68,68,0.25)' },
                        }}>
                        {deleting ? <CircularProgress size={13} sx={{ color: '#f87171' }} /> : null}
                        Delete
                    </Box>
                </DialogActions>
            </Dialog>
        </BrandManageGate>
    );
}
